/**
 * ONE LAZY ROUTE, DONE ONCE.
 *
 * ── THE MISMATCH THIS RESOLVES ──────────────────────────────────────────────
 * The router's contract is SYNCHRONOUS: `render()` assigns `teardown` and moves
 * on, and the next navigation calls whatever is in that variable. Code
 * splitting is ASYNCHRONOUS: `await import()` resolves on a later microtask, or
 * on a later network round trip. Written out at each call site, the join
 * between the two is four easy mistakes —
 *
 *   · the `case` returns before the screen exists, so nothing is torn down;
 *   · the import resolves after the player has navigated away and paints the
 *     Shop over the match (a slow network and a fast thumb are not rare on a
 *     phone);
 *   · a chunk that 404s after a deploy leaves a blank page that looks like it
 *     is still loading;
 *   · the screen's own teardown is dropped on the floor.
 *
 * `mountLazy` is those four decisions in one place. It returns a teardown
 * IMMEDIATELY; that teardown flips a flag so a late resolution mounts nothing
 * (and, if it already mounted, tears itself down); and a rejected import
 * renders a real message rather than nothing. `engine/meta/route.ts`'s
 * `mountMetaRoute` is the same discipline specialised to the meta shell —
 * it predates this helper and keeps its own copy because it must not import
 * anything from `engine/layout` (it is imported BY `routes.ts`, and the cycle
 * would be real).
 *
 * ── WHAT THE WAIT LOOKS LIKE ────────────────────────────────────────────────
 * It used to be one paragraph reading "Loading…" — honest, and the longest
 * uninterrupted moment of nothing in the product, since a cold visitor on a
 * phone waits it out before the game has said anything to them. It is now
 * `mountLoadingScreen` (`engine/rewards/loading-screen.ts`): the arena splash,
 * a baked model cut-out standing on it, pulsing pips and a progress bar.
 *
 * That component was written as a DROP-IN for this file and reproduces both of
 * this route's testid contracts exactly — `lazy-route-loading` carrying
 * `t('meta.loading')` while a chunk is in flight, and `lazy-route-failed` with
 * `role="alert"` carrying `t('meta.chunk_failed')` when the import rejects — so
 * `test/lazy-route-clears.test.ts` holds unchanged. Do not rename them.
 *
 * ── LAYER (ADR-0005) ────────────────────────────────────────────────────────
 * SHELL, and the loading screen is imported STATICALLY and by path rather than
 * through `engine/rewards`'s barrel. Both are deliberate: the thing that covers
 * a chunk download cannot itself be behind a chunk download, and the barrel
 * would drag the chest, the crowns reveal and the daily modal — none of which a
 * first paint needs — in with it.
 */
import { mountLoadingScreen, type LoadingScreenHandle } from '../rewards/loading-screen';
import { replaceChildren } from './dom';

/** What a lazily-loaded screen is: render into `root`, return its teardown. */
export type LazyScreen = (root: HTMLElement) => () => void;

/**
 * Mount a screen that has to be downloaded first, and return its teardown now.
 *
 * ```ts
 * teardown = mountLazy(app, async () => {
 *   const { renderDropTablesScreen } = await import('./engine/crates');
 *   return (root) => renderDropTablesScreen(root, { onClose: () => navigate('/play') });
 * });
 * ```
 *
 * `load` returns the RENDERER rather than calling it, so the whole of the
 * "did the player navigate away while this was in flight?" question is answered
 * here, once, instead of inside every screen's module.
 */
export function mountLazy(root: HTMLElement, load: () => Promise<LazyScreen>): () => void {
  let live = true;
  let inner: (() => void) | null = null;

  /*
   * CLEAR THE OUTGOING SCREEN NOW, not when the chunk lands.
   *
   * The router's teardown contract is "stop being interactive", and several
   * screens honour exactly that and no more — core's login screen writes
   * `root.innerHTML` and returns a teardown that only removes its submit
   * listener. Nothing else was clearing `root`, so between `render()` choosing
   * a lazy route and the chunk resolving, the PREVIOUS screen stayed on the
   * page; and a screen that renders by appending then drew itself around the
   * corpse. Signing in left the game mounted under the still-visible sign-in
   * form.
   *
   * Two frames of "Loading…" is the honest state and the one the fifth bullet
   * in this file's own header asks for. Doing it here rather than in each
   * screen means no future screen can forget.
   */
  replaceChildren(root);
  let loading: LoadingScreenHandle | null = null;

  const attempt = (): void => {
    loading?.destroy();
    replaceChildren(root);
    loading = mountLoadingScreen(root, { kind: 'chunk' });
    void load().then(
      (render) => {
        if (!live) return;
        // Drop the loading screen BEFORE the screen renders. Many screens
        // mount with `root.append(...)` rather than replacing — legitimately,
        // they were handed the node — so a placeholder still present here
        // becomes a sibling of the screen instead of being replaced by it.
        // `#app` is a growing flex item, so the extra node made the match
        // canvas ~88px taller than the window and the page scrolled.
        loading?.destroy();
        loading = null;
        replaceChildren(root);
        inner = render(root);
      },
      (err: unknown) => {
        // A chunk that 404s after a deploy is the realistic case, and it is
        // indistinguishable from "still loading" unless it is said out loud —
        // and a dead end unless the player can try again from right here.
        if (!live) return;
        console.error('[route] a lazily-loaded screen failed to load', err);
        if (loading !== null) {
          loading.fail(undefined, attempt);
          return;
        }
        replaceChildren(root);
      },
    );
  };
  attempt();

  return () => {
    live = false;
    loading?.destroy();
    loading = null;
    inner?.();
    inner = null;
    // The screen's own teardown is not required to empty the node it was given,
    // so the router's next screen must not inherit its markup.
    replaceChildren(root);
  };
}
